import axios from 'axios';
import getApiInstance from '../common/apiInstance';
import { getApiErrorMessage } from '../common/apiErrorMessage';
import {
  GradeReviewBody,
  GradeReviewResultDto,
  LineDetailDto,
  LineDrillMove,
  LineDrillResultDto,
  LineListItemDto,
  ReviewCardDto,
  ReviewCounts,
  CardManageItemDto,
  CourseManageCardsDto,
  ManageSummaryDto,
  BulkDeleteResultDto,
  SrsCountsDto,
  SrsDeck,
  SrsStatsDto,
  EMPTY_SRS_COUNTS,
} from './types';
import type { PositionReviewBucket } from './reviewPositionBucket';

const srsApi = getApiInstance('/srs', 'no-settings');

// SRS queue/stats must never be served from a stale browser cache (304), or the
// review board can show "caught up" while badge counts still show due cards.
const SRS_GET_CONFIG = {
  headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
} as const;

/** Cache-bust param so the browser never reuses a stale empty SRS queue (304). */
const srsGetParams = (params: Record<string, string | number>): Record<string, string | number> => ({
  ...params,
  _: Date.now(),
});

// Event broadcast on the window after a review/drill is recorded, so the nav
// badge can refresh its counts without a shared store.
export const REVIEW_UPDATED_EVENT = 'review:updated';

// How many position cards to pull when splitting due counts by source, and when
// serving a bucket-filtered review session (must use the same window or badge
// counts can exceed what limit=20 serving finds).
export const POSITION_REVIEW_FETCH_LIMIT = 500;
export const PUZZLE_REVIEW_FETCH_LIMIT = 20;

const reviewQueueCache = new Map<
  string,
  { fetchedAt: number; generation: number; cards: ReviewCardDto[] }
>();
const REVIEW_QUEUE_CACHE_MS = 5_000;
let reviewQueueCacheGeneration = 0;

export const invalidateReviewQueueCache = (): void => {
  reviewQueueCache.clear();
  reviewQueueCacheGeneration += 1;
};

export const notifyReviewUpdated = (): void => {
  invalidateReviewQueueCache();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(REVIEW_UPDATED_EVENT));
  }
};

const fetchStats = async (deck: SrsDeck): Promise<SrsStatsDto | null> => {
  try {
    const { data, status } = await srsApi.get<SrsStatsDto>('/stats', {
      ...SRS_GET_CONFIG,
      params: srsGetParams({ deck }),
      skipGlobalErrorToast: true,
      validateStatus: (s) => s === 200 || s === 401,
    });
    if (status === 401) {
      return null;
    }
    return data;
  } catch {
    return null;
  }
};

/** Unified badge counts from GET /srs/counts. */
export const fetchSrsCounts = async (): Promise<SrsCountsDto> => {
  try {
    const { data, status } = await srsApi.get<SrsCountsDto>('/counts', {
      ...SRS_GET_CONFIG,
      params: srsGetParams({}),
      skipGlobalErrorToast: true,
      skipAuthRedirect: true,
      validateStatus: (s) => s === 200 || s === 401,
    });
    if (status === 401 || !data) {
      return EMPTY_SRS_COUNTS;
    }
    return {
      all: data.all ?? 0,
      courses: data.courses ?? 0,
      games: data.games ?? 0,
      puzzles: data.puzzles ?? 0,
      lines: data.lines ?? 0,
      other: data.other ?? 0,
      moveDueByCourseSlug: data.moveDueByCourseSlug ?? {},
      lineReviewByCourseSlug: data.lineReviewByCourseSlug ?? {},
    };
  } catch {
    return EMPTY_SRS_COUNTS;
  }
};

// Aggregate servable queue lengths (and line count) into the badge shape.
export const fetchReviewCounts = async (): Promise<ReviewCounts> => {
  const counts = await fetchSrsCounts();
  return {
    all: counts.all,
    courses: counts.courses,
    games: counts.games,
    puzzles: counts.puzzles,
    lines: counts.lines,
    other: counts.other,
  };
};

export const fetchReviewStats = fetchStats;

// GET /srs/review?deck=...
export const fetchReviewQueue = async (
  deck: SrsDeck,
  limit = 20,
  courseSlug?: string,
  bucket?: PositionReviewBucket,
): Promise<ReviewCardDto[]> => {
  const cacheKey = `${deck}:${limit}:${courseSlug ?? ''}:${bucket ?? ''}`;
  const cached = reviewQueueCache.get(cacheKey);
  if (
    cached &&
    cached.generation === reviewQueueCacheGeneration &&
    Date.now() - cached.fetchedAt < REVIEW_QUEUE_CACHE_MS
  ) {
    return cached.cards;
  }

  const generationAtStart = reviewQueueCacheGeneration;

  try {
    const params: Record<string, string | number> = { deck, limit };
    if (courseSlug) {
      params.courseSlug = courseSlug;
    }
    if (bucket) {
      params.bucket = bucket;
    }
    const { data, status } = await srsApi.get<{
      deck: SrsDeck;
      cards: ReviewCardDto[];
    }>('/review', {
      ...SRS_GET_CONFIG,
      params: srsGetParams(params),
      skipGlobalErrorToast: true,
      validateStatus: (s) => s === 200 || s === 401,
    });
    if (status === 401 || !data?.cards) {
      return [];
    }
    if (generationAtStart === reviewQueueCacheGeneration) {
      reviewQueueCache.set(cacheKey, {
        fetchedAt: Date.now(),
        generation: reviewQueueCacheGeneration,
        cards: data.cards,
      });
    }
    return data.cards;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load review queue' }),
    );
  }
};

// GET /srs/cards/:cardId
export const fetchReviewCard = async (
  cardId: string,
): Promise<ReviewCardDto | null> => {
  try {
    const { data } = await srsApi.get<{ card: ReviewCardDto }>(
      `/cards/${encodeURIComponent(cardId)}`,
      SRS_GET_CONFIG,
    );
    return data?.card ?? null;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load review card' }),
    );
  }
};

// POST /srs/review/:cardId
export const gradeReview = async (
  cardId: string,
  body: GradeReviewBody,
): Promise<GradeReviewResultDto | null> => {
  try {
    const { data } = await srsApi.post<GradeReviewResultDto>(
      `/review/${encodeURIComponent(cardId)}`,
      body,
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to record review' }),
    );
  }
};

// POST /srs/missed-position — enroll a position missed in the replay trainer.
// Server-gated by the addMissedPositionsToSrs setting; best-effort so a failure
// never interrupts training.
export type EnrollMissedPositionInput = {
  fen: string;
  expectedUci: string;
  setupFen?: string;
  setupUci?: string;
  /** Source-game players, surfaced on the review board. */
  white?: string;
  black?: string;
  whiteElo?: number;
  blackElo?: number;
  timeControl?: string;
  timeClass?: string;
  date?: string;
  result?: string;
  eco?: string;
  opening?: string;
  event?: string;
  openingSans?: string[];
};

export type EnrollMissedReplayPositionResult = {
  created: boolean;
  skipped: boolean;
  /** True when the request failed (network/validation), not when a card already exists. */
  failed: boolean;
};

export const enrollMissedReplayPosition = async (
  input: EnrollMissedPositionInput,
): Promise<EnrollMissedReplayPositionResult> => {
  try {
    const { data, status } = await srsApi.post<{
      created: boolean;
      skipped: boolean;
    }>('/missed-position', input, {
      skipGlobalErrorToast: true,
      validateStatus: (s) => s === 200 || s === 201 || s === 401,
    });
    if (status === 401) {
      return { created: false, skipped: false, failed: true };
    }
    const created = data?.created === true;
    const skipped = data?.skipped === true;
    if (created) {
      notifyReviewUpdated();
    }
    return { created, skipped, failed: false };
  } catch {
    // Swallow: enrollment is a side effect of training, not the main flow.
    return { created: false, skipped: false, failed: true };
  }
};

/** Grade an existing position SRS card from the replay trainer. */
export const gradeReplayPosition = async (input: {
  fen: string;
  expectedUci: string;
  isCorrect?: boolean;
}): Promise<void> => {
  try {
    const { data } = await srsApi.post<{ graded: boolean }>(
      '/replay-position/grade',
      input,
      {
        skipGlobalErrorToast: true,
        validateStatus: (s) => s === 200 || s === 401,
      },
    );
    if (data?.graded) {
      notifyReviewUpdated();
    }
  } catch {
    // Swallow: grading is a side effect of training, not the main flow.
  }
};

// GET /srs/lines
export const fetchLines = async (limit = 50): Promise<LineListItemDto[]> => {
  try {
    const { data } = await srsApi.get<{ lines: LineListItemDto[] }>('/lines', {
      params: { limit },
    });
    return data?.lines ?? [];
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load lines' }),
    );
  }
};

// GET /srs/lines/:lineId
export const fetchLine = async (
  lineId: string,
): Promise<LineDetailDto | null> => {
  try {
    const { data } = await srsApi.get<LineDetailDto>(
      `/lines/${encodeURIComponent(lineId)}`,
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load line' }),
    );
  }
};

// POST /srs/lines/:lineId/result
export const submitLineResult = async (
  lineId: string,
  perMove: LineDrillMove[],
): Promise<LineDrillResultDto | null> => {
  try {
    const { data } = await srsApi.post<LineDrillResultDto>(
      `/lines/${encodeURIComponent(lineId)}/result`,
      { perMove },
    );
    return data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return null;
    }
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to record line drill' }),
    );
  }
};

// DELETE /srs/cards/:cardId
export const deleteReviewCard = async (cardId: string): Promise<boolean> => {
  try {
    const { status } = await srsApi.delete(
      `/cards/${encodeURIComponent(cardId)}`,
      { validateStatus: (s) => s === 204 || s === 404 },
    );
    if (status === 204) {
      notifyReviewUpdated();
    }
    return status === 204;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to delete card' }),
    );
  }
};

// DELETE /srs/lines/:lineId
export const deleteReviewLine = async (lineId: string): Promise<boolean> => {
  try {
    const { status } = await srsApi.delete(
      `/lines/${encodeURIComponent(lineId)}`,
      { validateStatus: (s) => s === 204 || s === 404 },
    );
    if (status === 204) {
      notifyReviewUpdated();
    }
    return status === 204;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to delete line' }),
    );
  }
};

// GET /srs/manage/summary
export const fetchManageSummary = async (): Promise<ManageSummaryDto> => {
  try {
    const { data } = await srsApi.get<ManageSummaryDto>('/manage/summary');
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load review inventory' }),
    );
  }
};

// GET /srs/manage/cards?deck=...
export const fetchManageCards = async (
  deck: SrsDeck,
  limit = 500,
): Promise<CardManageItemDto[]> => {
  try {
    const { data } = await srsApi.get<{ cards: CardManageItemDto[] }>(
      '/manage/cards',
      { params: { deck, limit } },
    );
    return data?.cards ?? [];
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load cards' }),
    );
  }
};

// GET /srs/manage/courses/:courseSlug/cards
export const fetchCourseManageCards = async (
  courseSlug: string,
  limit = 500,
): Promise<CourseManageCardsDto> => {
  try {
    const { data } = await srsApi.get<CourseManageCardsDto>(
      `/manage/courses/${encodeURIComponent(courseSlug)}/cards`,
      { params: { limit } },
    );
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to load course cards' }),
    );
  }
};

// DELETE /srs/manage/cards?deck=...
export const deleteAllManageCards = async (
  deck: SrsDeck,
): Promise<BulkDeleteResultDto> => {
  try {
    const { data } = await srsApi.delete<BulkDeleteResultDto>('/manage/cards', {
      params: { deck },
    });
    notifyReviewUpdated();
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to delete cards' }),
    );
  }
};

// DELETE /srs/manage/lines
export const deleteAllManageLines = async (): Promise<BulkDeleteResultDto> => {
  try {
    const { data } = await srsApi.delete<BulkDeleteResultDto>('/manage/lines');
    notifyReviewUpdated();
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to delete lines' }),
    );
  }
};

// DELETE /srs/manage/all
export const deleteAllReviewData = async (): Promise<BulkDeleteResultDto> => {
  try {
    const { data } = await srsApi.delete<BulkDeleteResultDto>('/manage/all');
    notifyReviewUpdated();
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to delete review data' }),
    );
  }
};

// DELETE /srs/manage/courses/:courseSlug
export const deleteCourseReviewCards = async (
  courseSlug: string,
): Promise<BulkDeleteResultDto> => {
  try {
    const { data } = await srsApi.delete<BulkDeleteResultDto>(
      `/manage/courses/${encodeURIComponent(courseSlug)}`,
    );
    notifyReviewUpdated();
    return data;
  } catch (error) {
    throw new Error(
      getApiErrorMessage(error, { action: 'Failed to delete course cards' }),
    );
  }
};
